/**
 * Content-Disposition helpers, safe for filenames with code points above
 * U+00FF (CJK, emoji, Cyrillic, accented Latin). Pure + browser-safe — this
 * module lives in @papan/types and must use only JS built-ins.
 *
 * Why: `Content-Disposition` header values are byte strings; Node's setHeader
 * throws ERR_INVALID_CHAR on any code point > U+00FF, so a raw non-ASCII
 * filename crashes the response mid-write (a 500 the user sees as "the file
 * won't open"). We percent-encode both parameters so the wire is pure ASCII.
 */

/**
 * Build a Content-Disposition header value that is safe for any filename.
 *
 * encodeURIComponent escapes ", \, CR, LF AND every non-ASCII byte, so the
 * emitted value is pure ASCII. We emit BOTH RFC 6266 parameters: filename="…"
 * as the (percent-encoded) ASCII fallback for old clients, and
 * filename*=UTF-8''… as the real name for RFC 6266-aware clients.
 */
export function contentDisposition(name: string, type: 'attachment' | 'inline' = 'attachment'): string {
  const enc = encodeURIComponent(name)
  return `${type}; filename="${enc}"; filename*=UTF-8''${enc}`
}

/**
 * Parse a filename out of a Content-Disposition header, returning the DECODED
 * real name. Prefers filename*=UTF-8''<pct> (RFC 6266 mandate), then
 * filename="<val>" (also percent-decoded — our own servers put the encoded
 * name there), then the caller's fallback. Malformed percent-encoding degrades
 * to the raw match rather than throwing (decodeURIComponent throws on a bare
 * `%` or a lone surrogate, and this runs on the download hot path).
 */
export function parseContentDispositionFilename(
  header: string | null | undefined,
  fallback: string,
): string {
  if (!header) return fallback
  const safeDecode = (v: string): string => {
    try {
      return decodeURIComponent(v)
    } catch {
      return v
    }
  }
  const star = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header)
  if (star?.[1]) return safeDecode(star[1].trim())
  const plain = /filename\s*=\s*"([^"]*)"/i.exec(header)
  if (plain?.[1]) return safeDecode(plain[1])
  return fallback
}
